import firebase from "firebase";
import React, { useEffect, useRef } from "react";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { getAppVersion } from "@desktop/ToDesktopService";
import { addOnTrayClickEventListener } from "@desktop/ToDestkopEventListeners";
import { auth } from "@firebase-local/firebase";
import useSessionStorage, { SessionStorageKeys } from "@hooks/useSessionStorage";
import { MenubarTrayClick } from "../../AmplitudeDesktopAppEvents";
import MenubarApp from "../../components/MenubarApp";
import DesktopAppMenubarLanding from "./DesktopAppMenubarLanding";

// Route: /desktop/menubar
function DesktopAppMenubarWindow() {
    const amplitudeService = AmplitudeService.getInstance();

    const { user }: UserContextType = useUserContext();
    const [hasSeenReloadScreen, setHasSeenReloadScreen] = useSessionStorage(
        SessionStorageKeys.HasSeenReloadScreen,
        false,
    );

    // NOTE: DesktopAppMainWindow.tsx determines which window (main or menubar)
    //  should show based on auth status
    const isAuth = useRef(null);

    useEffect(() => {
        if (window.todesktop) {
            // Log an event whenever the user toggles the menubar window to display.
            const trayClickListenerCb = () => {
                amplitudeService.logEvent(
                    new MenubarTrayClick({
                        appVersion: getAppVersion(),
                    }),
                );
            };

            const promise = addOnTrayClickEventListener(trayClickListenerCb);
            return () => promise.then(async (unsubscribe: (() => Promise<void>) | null) => await unsubscribe?.());
        }

        const unsubscribe = auth.onAuthStateChanged(async function (stateChangeUser: firebase.User | null) {
            if (!isAuth.current) {
                isAuth.current = stateChangeUser && stateChangeUser.emailVerified;
            }
        });
        return () => {
            unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (user && user.emailVerified && (window.todesktop || window.location.origin.includes("localhost"))) {
            isAuth.current = true;

            // Reset the reload screen flag so that the next time auth isn't found,
            // it will display the reload screen instead of the signin screen
            if (hasSeenReloadScreen) {
                setHasSeenReloadScreen(false);
            }
        }
    }, [user, user?.emailVerified]);

    return isAuth.current ? <MenubarApp /> : <DesktopAppMenubarLanding />;
}
export default DesktopAppMenubarWindow;
